import React, { useState, useEffect } from 'react';
import { 
  MapPinIcon, 
  GlobeAltIcon,
  BuildingOfficeIcon,
  SignalIcon,
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  WifiIcon,
  GlobeAsiaAustraliaIcon,
  ServerStackIcon,
  CurrencyDollarIcon,
  MapIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import useTranslation from '../services/translationService';

const IPAddressDetails = ({ ipAddress, onClose }) => {
  const { t } = useTranslation();
  const [ipInfo, setIpInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIPInfo = async () => {
      if (!ipAddress || ipAddress === 'unknown') {
        setError('Invalid IP address');
        setLoading(false);
        return;
      }

      try {
        // Using ipapi.co free tier (no API key required for limited requests)
        const response = await fetch(`https://ipapi.co/${ipAddress}/json/`);
        if (!response.ok) {
          throw new Error('Failed to fetch IP information');
        }
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.reason || 'IP lookup failed');
        }
        
        setIpInfo(data);
      } catch (err) {
        console.error('Error fetching IP info:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIPInfo();
  }, [ipAddress]);

  const getMapUrl = () => {
    if (!ipInfo || !ipInfo.latitude || !ipInfo.longitude) return null;
    
    // Using OpenStreetMap static image
    const lat = ipInfo.latitude;
    const lon = ipInfo.longitude;
    const zoom = 10;
    
    // Create a simple static map URL using OpenStreetMap tiles
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lon-0.1},${lat-0.1},${lon+0.1},${lat+0.1}&layer=mapnik&marker=${lat},${lon}`;
  };

  const getRiskLevel = (ipInfo) => {
    // Simple risk assessment based on available data
    if (!ipInfo) return 'unknown';
    
    // Check for VPN/Proxy/Tor
    if (ipInfo.threat?.is_vpn || ipInfo.threat?.is_proxy || ipInfo.threat?.is_tor) {
      return 'high';
    }
    
    // Check for suspicious countries (you can customize this list)
    const highRiskCountries = ['KP', 'IR', 'SY']; // Example list
    if (highRiskCountries.includes(ipInfo.country_code)) {
      return 'high';
    }
    
    // Check if it's a known datacenter
    if (ipInfo.org?.toLowerCase().includes('datacenter') || 
        ipInfo.org?.toLowerCase().includes('hosting') ||
        ipInfo.org?.toLowerCase().includes('cloud')) {
      return 'medium';
    }
    
    return 'low';
  };

  const riskLevel = getRiskLevel(ipInfo);
  const riskColors = {
    low: 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800',
    medium: 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-800',
    high: 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800',
    unknown: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700'
  };

  const riskIcons = {
    low: CheckCircleIcon,
    medium: ExclamationTriangleIcon,
    high: XCircleIcon,
    unknown: ShieldCheckIcon
  };

  // Detect if device is mobile
  const isMobile = window.innerWidth < 768;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
        <div className={`bg-white dark:bg-gray-800 shadow-2xl p-8 max-w-sm w-full
          ${isIOS ? 'rounded-3xl' : 'rounded-2xl'}
          animate-slideUp`}>
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-100 dark:border-blue-900 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="text-gray-700 dark:text-gray-200 text-center font-medium">
              {t('ipDetails.loading', 'Loading IP information...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4 z-50 animate-fadeIn">
      <div className={`bg-white dark:bg-gray-800 w-full md:max-w-4xl max-h-[95vh] md:max-h-[90vh] flex flex-col
        ${isIOS ? 'rounded-t-3xl md:rounded-3xl' : 'rounded-t-2xl md:rounded-2xl'}
        ${isMobile ? 'animate-slideUp' : 'animate-scaleIn'}
        shadow-2xl`}>
        {/* Header */}
        <div className={`sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 
          ${isIOS ? 'px-6 py-5' : 'px-4 md:px-6 py-4'}
          ${isIOS ? 'rounded-t-3xl' : 'rounded-t-2xl md:rounded-t-2xl'}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                <GlobeAltIcon className="w-5 h-5 text-white" />
              </div>
              <span className="truncate">{t('ipDetails.title', 'IP Address Details')}</span>
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 
                hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={`${isIOS ? 'px-6 py-5' : 'px-4 md:px-6 py-4 md:py-6'}`}>
            {error ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
                  <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            ) : ipInfo ? (
              <div className="space-y-4 md:space-y-6">
                {/* IP and Risk Level */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {t('ipDetails.ipAddress', 'IP Address')}
                    </p>
                    <p className="text-xl md:text-2xl font-mono font-bold text-gray-900 dark:text-white">
                      {ipAddress}
                    </p>
                  </div>
                  <div className={`px-4 py-2.5 rounded-xl border-2 inline-flex items-center gap-2 ${riskColors[riskLevel]}`}>
                    {React.createElement(riskIcons[riskLevel], {
                      className: 'w-5 h-5'
                    })}
                    <span className="font-semibold text-sm">
                      {t(`ipDetails.risk.${riskLevel}`, riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1) + ' Risk')}
                    </span>
                  </div>
                </div>

              {/* Location Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                      <MapPinIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('ipDetails.location', 'Location')}
                    </h3>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                    {ipInfo.country_name && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          <GlobeAsiaAustraliaIcon className="w-4 h-4" />
                          {t('ipDetails.country', 'Country')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white text-right">
                          {ipInfo.country_name} {ipInfo.country_code && `(${ipInfo.country_code})`}
                        </span>
                      </div>
                    )}
                    {ipInfo.region && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.region', 'Region')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.region}
                        </span>
                      </div>
                    )}
                    {ipInfo.city && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.city', 'City')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.city}
                        </span>
                      </div>
                    )}
                    {ipInfo.postal && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.postal', 'Postal Code')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.postal}
                        </span>
                      </div>
                    )}
                    {ipInfo.latitude && ipInfo.longitude && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.coordinates', 'Coordinates')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white font-mono text-sm">
                          {ipInfo.latitude.toFixed(4)}, {ipInfo.longitude.toFixed(4)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                      <WifiIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('ipDetails.network', 'Network')}
                    </h3>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                    {ipInfo.org && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          <ServerStackIcon className="w-4 h-4" />
                          {t('ipDetails.isp', 'ISP')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white text-right text-sm">
                          {ipInfo.org}
                        </span>
                      </div>
                    )}
                    {ipInfo.asn && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.asn', 'ASN')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.asn}
                        </span>
                      </div>
                    )}
                    {ipInfo.timezone && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.timezone', 'Timezone')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.timezone}
                        </span>
                      </div>
                    )}
                    {ipInfo.currency && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.currency', 'Currency')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Map */}
              {getMapUrl() && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                      <MapIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('ipDetails.map', 'Location Map')}
                    </h3>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
                    <iframe
                      width="100%"
                      height={isMobile ? "250" : "400"}
                      frameBorder="0"
                      scrolling="no"
                      src={getMapUrl()}
                      title="IP Location Map"
                      className="w-full"
                    />
                  </div>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 text-center italic">
                    {t('ipDetails.mapNote', 'Approximate location based on IP geolocation')}
                  </p>
                </div>
              )}

              {/* Additional Security Info */}
              {(ipInfo.threat || ipInfo.in_eu !== undefined) && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg">
                      <ShieldCheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('ipDetails.security', 'Security Information')}
                    </h3>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                    {ipInfo.in_eu !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('ipDetails.euLocation', 'EU Location')}:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {ipInfo.in_eu ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </span>
                      </div>
                    )}
                    {ipInfo.threat?.is_vpn !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">VPN:</span>
                        <span className={`font-medium ${ipInfo.threat.is_vpn ? 'text-red-600' : 'text-green-600'}`}>
                          {ipInfo.threat.is_vpn ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </span>
                      </div>
                    )}
                    {ipInfo.threat?.is_proxy !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Proxy:</span>
                        <span className={`font-medium ${ipInfo.threat.is_proxy ? 'text-red-600' : 'text-green-600'}`}>
                          {ipInfo.threat.is_proxy ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </span>
                      </div>
                    )}
                    {ipInfo.threat?.is_tor !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Tor:</span>
                        <span className={`font-medium ${ipInfo.threat.is_tor ? 'text-red-600' : 'text-green-600'}`}>
                          {ipInfo.threat.is_tor ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 bg-gradient-to-t from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 
          border-t border-gray-200 dark:border-gray-700 
          ${isIOS ? 'px-6 py-5 pb-safe' : 'px-4 md:px-6 py-4'}`}>
          <button
            onClick={onClose}
            className={`w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 
              hover:from-blue-600 hover:to-indigo-700 text-white font-medium
              transition-all transform hover:scale-[1.02] active:scale-[0.98]
              ${isIOS ? 'rounded-2xl' : 'rounded-xl'}
              shadow-lg`}
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IPAddressDetails;
